import * as typeformEmbed from "@typeform/embed";
import {
  toWSDateString,
  useIsL,
  useIsXL,
  useModalContext,
  WSElement,
  WSPage,
  WSText
} from "@wingspanhq/fe-component-library";
import { DocumentStatus } from "@wingspanhq/files/dist/lib/interfaces";

import {
  RequirementStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces/eligibilityRequirement";
import { ToDoStatus } from "@wingspanhq/users/dist/lib/interfaces";
import flatten from "lodash/flatten";
import React from "react";
import { useHistory } from "react-router";
import { useBookkeepingFilters } from "../../Bookkeeping/screens/dashboard/BookkeepingIndex";
import { useBrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { OverlaySpinner } from "../../components/OverlaySpinner";
import {
  useBookkeepingStatus,
  useTransactions
} from "../../query/bookkeeping/queries";
import { useCMSWebflowEnterprise } from "../../query/cms/queries";
import {
  useCountersignDocument,
  useSignDocument
} from "../../query/files/mutations";
import { useUserId } from "../../query/hooks/helpers";
import {
  useCollaboratorsQuery,
  useInvoicesQuery,
  useMemberClientsQuery
} from "../../query/payments/queries";
import {
  getClientCompany,
  getClientName,
  getCollaboratorCompany,
  getCollaboratorName
} from "../../query/payments/selectors";
import { getCurrentEntepriseId } from "../../query/platform/selectors";
import {
  useActivities,
  useMemberProfile,
  useUserProfile
} from "../../query/users/queries";
import { WSQueries } from "../../query/WSQuery";
import {
  getAuthorizedAccountName,
  useAuthorizedScopeGroups
} from "../../Settings/utils/teamUtils";
import { useWSStore } from "../../store";
import {
  getSegmentAnonymousIdFromLocalStorage,
  track
} from "../../utils/analytics";
import { openInNewTab } from "../../utils/openInNewTab";
import {
  DashboardToDo,
  DoneEmptyState,
  InProgressEmptyState,
  ToDoEmptyState
} from "../components/DashboardToDo/DashboardToDo";
import {
  LoomDemoModal,
  LOOM_DEMO_MODAL
} from "../components/LoomDemoModal/LoomDemoModal";
import {
  getActivateTeladocToDoStatus,
  getAddYourFirstIncomeTransactionToDoStatus,
  getSendYourFirstInvoiceToDoStatus,
  groupTasksByCategory,
  IDashboardToDo,
  TodoCategory,
  WSAppFeatures
} from "../utils/dashboardUtils";
import styles from "./MemberDashboard.module.scss";

export interface MemberDashboardProps {}

export const MemberDashboard: React.FC<MemberDashboardProps> = () => {
  const history = useHistory();
  const userId = useUserId();
  const store = useWSStore();
  const userQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const activitiesQuery = useActivities(userId);
  const bookkeepingStatusQuery = useBookkeepingStatus();
  const invoicesQuery = useInvoicesQuery();
  const bkTxnFilters = useBookkeepingFilters();
  const transactionsQuery = useTransactions(bkTxnFilters.filters, {
    enabled: bookkeepingStatusQuery.data?.enabled
  });
  const enterpriseId = getCurrentEntepriseId(store, activitiesQuery.data);
  const enterpriseQuery = useCMSWebflowEnterprise(enterpriseId || "", {
    enabled: !!enterpriseId
  });
  const { openModal } = useModalContext();
  const isL = useIsL();
  const isXL = useIsXL();
  const memberClientsQuery = useMemberClientsQuery();
  const collaboratorsQuery = useCollaboratorsQuery();
  const [signDocument, signDocumentMeta] = useSignDocument();
  const [
    countersignDocument,
    countersignDocumentMeta
  ] = useCountersignDocument();

  useBrowserPageTitle("Wingspan - Dashboard");

  const {
    hasAdminScope,
    hasCollaboratorsScope,
    hasPaymentsScope,
    hasFinancesScope
  } = useAuthorizedScopeGroups();

  return (
    <WSPage>
      {(signDocumentMeta.isLoading || countersignDocumentMeta.isLoading) && (
        <OverlaySpinner />
      )}
      <WSQueries
        queries={{
          userQuery,
          memberQuery,
          activitiesQuery
        }}
      >
        {({
          userQuery: { data: user },
          memberQuery: { data: member },
          activitiesQuery: { data: activities }
        }) => {
          const enterprise = enterpriseQuery.data;

          const { src: videoSrc } =
            enterprise && enterprise.exploreVideo && enterprise.exploreVideo.src
              ? enterprise.exploreVideo
              : {
                  src:
                    "https://www.loom.com/embed/f0e98f1f83d74ec396da6df1bbd2ad00"
                };
          const transactionsList = flatten(transactionsQuery.data || []);
          const tasks: IDashboardToDo[] = [];

          if (memberClientsQuery.data && hasCollaboratorsScope) {
            memberClientsQuery.data.forEach(memberClient => {
              (memberClient.eligibilityRequirements || []).forEach(
                eligibilityRequirement => {
                  if (
                    eligibilityRequirement.requirementType ===
                      RequirementType.Signature &&
                    eligibilityRequirement.status !== RequirementStatus.Complete
                  ) {
                    tasks.push({
                      title: eligibilityRequirement.document?.title || "",
                      description: `Your client ${getClientName(memberClient) ||
                        getClientCompany(
                          memberClient
                        )} sent you this document to sign.`,
                      icon: "clip",
                      status: ToDoStatus.ToDo,
                      onClick: () => {
                        signDocument({
                          documentId: eligibilityRequirement.documentId,
                          memberClietnId: memberClient.memberClientId
                        });
                      },
                      category: TodoCategory.DocumentsToSign
                    });
                  }
                }
              );
            });
          }

          if (collaboratorsQuery.data && hasCollaboratorsScope) {
            collaboratorsQuery.data.forEach(collaborator => {
              (collaborator.eligibilityRequirements || []).forEach(
                eligibilityRequirement => {
                  if (
                    eligibilityRequirement.requirementType ===
                      RequirementType.Signature &&
                    eligibilityRequirement.status ===
                      RequirementStatus.Complete &&
                    (eligibilityRequirement.document
                      ? eligibilityRequirement.document.status !==
                        DocumentStatus.Complete
                      : false)
                  ) {
                    tasks.push({
                      title: eligibilityRequirement.document?.title || "",
                      description: `Your collaborator ${getCollaboratorName(
                        collaborator
                      ) ||
                        getCollaboratorCompany(
                          collaborator
                        )} signed this document on ${toWSDateString(
                        (eligibilityRequirement.document as any)?.events
                          ?.memberSignedAt,
                        "monthDayYear"
                      )}.`,
                      icon: "clip",
                      status: ToDoStatus.ToDo,
                      onClick: () => {
                        countersignDocument({
                          documentId: eligibilityRequirement.documentId,
                          collaboratorId: collaborator.collaboratorId
                        });
                      },
                      category: TodoCategory.DocumentsToSign
                    });
                  }
                }
              );
            });
          }

          if (invoicesQuery.data && hasPaymentsScope) {
            tasks.push({
              title: "Send your first invoice",
              description:
                "Less digital paperwork to enjoy more of your time and money.",
              icon: "moneyRounded",
              status: getSendYourFirstInvoiceToDoStatus(
                invoicesQuery.data,
                activities
              ),
              onClick: () => {
                history.push("/member/invoices");
              },
              category: TodoCategory.General,
              feature: WSAppFeatures.Payments
            });
          }

          if (transactionsQuery.data && hasFinancesScope) {
            tasks.push({
              title: "Add your first income transaction",
              description:
                "Set up payments first because the best part of bookkeeping is a new direct deposit",
              icon: "moneyRounded",
              status: getAddYourFirstIncomeTransactionToDoStatus(
                transactionsList,
                activities
              ),
              onClick: () => {
                history.push(
                  "/member/bookkeeping/transactions/add-transaction"
                );
              },
              category: TodoCategory.General,
              feature: WSAppFeatures.Bookkeeping
            });
          }

          if (hasAdminScope) {
            tasks.push({
              title: "Watch the tour",
              description: "We'll walk you through your new back office.",
              icon: "chevronRightFilled",
              status:
                activities.events.watchTourVideoToDoStatus || ToDoStatus.ToDo,
              onClick: () => {
                if (isL || isXL) {
                  openModal(LOOM_DEMO_MODAL);
                } else {
                  openInNewTab(videoSrc);
                }
              },
              category: TodoCategory.General,
              canMarkAsDone: true,
              activityEventName: "watchTourVideoToDoStatus"
            });
          }

          if (hasAdminScope) {
            tasks.push({
              title: "Find your first deductible expense",
              description:
                "Our bookkeeping tools help you minimize taxes and hang onto more of your money.",
              icon: "clipboard",
              status:
                activities.events.findFirstDeductibleExpenseToDoStatus ||
                ToDoStatus.ToDo,
              onClick: () => {
                history.push("/member/bookkeeping");
              },
              category: TodoCategory.General,
              feature: WSAppFeatures.Bookkeeping,
              canMarkAsDone: true,
              activityEventName: "findFirstDeductibleExpenseToDoStatus"
            });
          }

          if (hasAdminScope) {
            tasks.push({
              title: "Activate Teledoc, financial advisor, and more",
              description:
                "Connect to the perks included in your membership and find the best rates possible for Insurance.",
              icon: "heart",
              status: getActivateTeladocToDoStatus(activities),
              onClick: () => {
                history.push("/member/benefits");
              },
              category: TodoCategory.General,
              feature: WSAppFeatures.Benefits
            });
          }

          if (hasAdminScope) {
            tasks.push({
              title: "See if an LLC is right for you",
              description:
                "We'll help you choose the right company structure and set it up compliantly.",
              icon: "institution",
              status:
                activities.events.knowingCompanyStructureToDoStatus ||
                ToDoStatus.ToDo,
              onClick: () => {
                track("Click Setup your business with an LLC", {
                  location: "Dashboard"
                });

                const anonymousId = getSegmentAnonymousIdFromLocalStorage();

                const typeFormReference = typeformEmbed.makePopup(
                  `https://wingspan.typeform.com/formation?name=${user.profile
                    .firstName +
                    " " +
                    user.profile.lastName}&userid=${userId}&email=${
                    user.email
                  }&anonymousid=${anonymousId}`,
                  {
                    onSubmit: event => {
                      track("Calendly Profile Page Viewed", {
                        responseId: event.response_id
                      });
                    }
                  }
                );
                typeFormReference.open();
              },
              category: TodoCategory.General,
              feature: WSAppFeatures.Business,
              canMarkAsDone: true,
              activityEventName: "knowingCompanyStructureToDoStatus"
            });
          }

          const todos = tasks.filter(task => task.status === ToDoStatus.ToDo);
          const inProgressTasks = tasks.filter(
            task => task.status === ToDoStatus.InProgress
          );
          const completedTasks = tasks.filter(
            task => task.status === ToDoStatus.Completed
          );

          const todosByCategories = groupTasksByCategory(todos);
          const categories = Object.keys(todosByCategories);
          const categoryNameMapping = {
            [TodoCategory.General]: "General",
            [TodoCategory.DocumentsToSign]: "Documents to sign"
          };

          return (
            <>
              <LoomDemoModal />
              <WSElement className={styles.pageContent} mx="L">
                <WSText.Heading1 mb="S" data-testid="gettingStartedTitle">
                  Welcome to Wingspan
                  {`, ${getAuthorizedAccountName(member, user)}`}.
                </WSText.Heading1>
                <WSText mb="XL" color="grey600">
                  We've outlined a few important tasks to get you off on the
                  right foot.
                </WSText>
                <WSText.Heading2 mb="M">To Do</WSText.Heading2>
                {categories.length > 0 ? (
                  categories.map((key, index) => (
                    <WSElement mb="M" key={key + index}>
                      <WSText.Heading4 mb="S">
                        {categoryNameMapping[key as TodoCategory]}
                      </WSText.Heading4>
                      {(todosByCategories[key as TodoCategory] || []).map(
                        (todo, index) => (
                          <DashboardToDo
                            todoType="toDO"
                            key={todo.title + index}
                            {...todo}
                            mb="S"
                          />
                        )
                      )}
                    </WSElement>
                  ))
                ) : (
                  <ToDoEmptyState />
                )}
                <WSText.Heading2 mb="M" mt="L">
                  In Progress
                </WSText.Heading2>
                {inProgressTasks.length > 0 ? (
                  inProgressTasks.map(todo => (
                    <DashboardToDo
                      todoType="inProgress"
                      key={todo.title}
                      {...todo}
                      mb="S"
                    />
                  ))
                ) : (
                  <InProgressEmptyState />
                )}
                <WSText.Heading2 mb="M" mt="L">
                  Done
                </WSText.Heading2>
                {completedTasks.length > 0 ? (
                  completedTasks.map(todo => (
                    <DashboardToDo
                      todoType="done"
                      key={todo.title}
                      {...todo}
                      mb="S"
                    />
                  ))
                ) : (
                  <DoneEmptyState />
                )}
              </WSElement>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
